import { resolveCountryBucket } from "@screencloud/billing-client-api";
import type { PixiWizardSelection } from "../../hooks/usePixiWizardState";
import {
  CurrencyCode,
  DISCOUNT_PERCENT,
  PER_DEVICE_VALUE_CENTS,
  PLAN_CONTENT,
  PlanContent,
  PlanId,
} from "../PlanBillingStep/constants";
import {
  resolveEffectiveDealId,
  resolveSelection,
} from "../PlanBillingStep/selection";
import {
  billingPeriodPriceCents,
  useOnboardingPlansContext,
} from "../PlanBillingStep/useOnboardingPlans";
import { ShippingRate, shippingRateFor } from "./shippingRates";
import { useShippingRates } from "./useShippingRates";

/**
 * The single pricing computation for the Pixi Bundle purchase, shared by the
 * Checkout step (which shows the shipping-inclusive grand total) and the
 * Complete step (which shows the subscription total and shipping separately).
 * Keeping it in one place stops the two screens' numbers from drifting.
 *
 * All amounts are in MINOR units (cents), matching the billing currency util.
 * When the customer-create response becomes the source of truth, repoint the
 * totals here rather than in each screen.
 */
export interface CheckoutPricing {
  planId: PlanId;
  planContent: PlanContent;
  currency: CurrencyCode;
  billingCycle: "year" | "month";
  licenses: number;
  shippingCountry: string;
  isFreeDevice: boolean;
  isDiscount: boolean;
  licencesCents: number;
  deviceSavingsCents: number;
  discountCents: number;
  shippingCents: number;
  /**
   * The shipping service actually available for the destination, taken from the
   * resolved rate (a destination may offer only express, or only standard) —
   * never assumed. undefined when no rate matches (no shipping available).
   */
  shippingService?: string;
  /** Licences minus any discount — EXCLUDES shipping. */
  subscriptionTotalCents: number;
  /** Subscription total plus shipping (free-device deal only). */
  grandTotalCents: number;
  rates: ShippingRate[];
}

export const useCheckoutPricing = (
  state: PixiWizardSelection,
): CheckoutPricing => {
  const { getPlan, isProTrialist } = useOnboardingPlansContext();
  const { rates } = useShippingRates();

  const { planId, currency, billingCycle, shippingCountry, licenses } =
    resolveSelection(state, isProTrialist);
  const plan = getPlan(planId, billingCycle, currency);
  const monthlyPlan = getPlan(planId, "month", currency);

  // Derive from the valid deal, not the raw persisted dealId: on a hard resume
  // straight to Checkout (the step is restored from localStorage) the user may
  // not have re-entered the Plan step to clear a stale free-device selected for
  // an `Other` shipping country, so validate it here too (GRWBKK-1730).
  const effectiveDealId = resolveEffectiveDealId(state);
  const isFreeDevice = effectiveDealId === "free-device";
  const isDiscount = effectiveDealId === "discount";

  // Yearly + free-device bills at the monthly rate × 12 (the free Pixi replaces
  // the annual discount) — see `billingPeriodPriceCents` (GRWBKK-1695).
  // Yearly + discount DISPLAYS the same full price (monthly rate × 12) so the
  // discount can be broken out as a visible line, but still CHARGES the annual
  // catalogue price (GRWBKK-1739) — see `subscriptionTotalCents` below.
  const isYearlyDiscount = isDiscount && billingCycle === "year";
  const licencesCents = plan
    ? billingPeriodPriceCents(
        plan,
        monthlyPlan,
        isFreeDevice || isYearlyDiscount,
      ) * licenses
    : 0;
  const deviceSavingsCents = PER_DEVICE_VALUE_CENTS[currency] * licenses;
  // Yearly discount: the exact gap between the full price and the charged
  // annual catalogue price, so licences − discount always equals the charge
  // (DISCOUNT_PERCENT is the marketing label, not the arithmetic). Monthly
  // keeps the rounded percentage cut (GRWBKK-1705).
  const annualCatalogueCents = plan ? plan.price * licenses : 0;
  const discountCents = isYearlyDiscount
    ? Math.max(licencesCents - annualCatalogueCents, 0)
    : Math.round((licencesCents * DISCOUNT_PERCENT) / 100);
  // `shippingCountry` is an ISO code from the dropdown; the API keys rates by
  // regional bucket, so translate before looking up the rate. `resolveCountryBucket`
  // (billing client) returns `null` for an unrecognised code — treat that as no
  // bucket so no rate matches (see GRWBKK SC-08).
  // Resolve the actual rate row: the user's picked service is only a preference —
  // the destination decides what's available (standard, express, or neither), so
  // the charged amount and the service both come from the matched rate, never a
  // hardcoded default.
  const shippingRate = shippingRateFor(rates, {
    countryBucket: resolveCountryBucket(shippingCountry) ?? undefined,
    currency,
    screenCount: licenses,
    shippingService: state.shippingService,
  });
  const shippingCents = shippingRate ? shippingRate.amount * 100 : 0;
  const shippingService = shippingRate?.service;

  // The discount deal always subtracts its discount from the displayed licence
  // price. Yearly this lands exactly on the annual catalogue price (the amount
  // charged — the ~17% saving is baked into that price, GRWBKK-1705/1739);
  // monthly it subtracts the rounded percentage as before.
  const subscriptionTotalCents =
    licencesCents - (isDiscount ? discountCents : 0);
  const grandTotalCents =
    subscriptionTotalCents + (isFreeDevice ? shippingCents : 0);

  return {
    planId,
    planContent: PLAN_CONTENT[planId],
    currency,
    billingCycle,
    licenses,
    shippingCountry,
    isFreeDevice,
    isDiscount,
    licencesCents,
    deviceSavingsCents,
    discountCents,
    shippingCents,
    shippingService,
    subscriptionTotalCents,
    grandTotalCents,
    rates,
  };
};
